import {useEffect, useMemo, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {useApolloClient, useMutation, useQuery} from '@apollo/client';
import {
  Alert,
  Button,
  Form,
  Input,
  Modal,
  Select,
  Spin,
  Tabs,
  Tag,
  Upload,
} from 'antd';
import {ArrowLeftOutlined, UploadOutlined} from '@ant-design/icons';
import moment from 'moment';
import ReactQuill from 'react-quill';
import {useSelector} from 'react-redux';
import {toast} from 'react-toastify';
import NewsModal from '../shared/NewsModal';
import {makeSelectSubTabOperations} from 'redux/store/auth';
import {NavTabsEnum, QUILL_FORMATS, QUILL_MODULES} from 'utils/constants';
import CompanySearch from '../shared/CompanySearch';
import {
  resizeImageFile,
  UploadDocumentToBucket,
} from 'pages/campaigns/new/screens/legalDocument/uploadDocument';
import {
  actionError,
  loadError,
  notDeployed,
  unionError,
} from '../shared/errors';
import {ActionButton, ChoiceGroup, Tip} from '../shared/ui';
import {REVIEW_SUB_TAB} from './NewsReview';
import {
  APPROVE_NEWS_DRAFT,
  GET_NEWS_DRAFT,
  REJECT_NEWS_DRAFT,
} from './resolvers';
import {DRAFT, LANGUAGE_LABEL, STATUS, TAG_OPTIONS, TYPE_OPTIONS} from './copy';
import {GET_FINANCIAL_INSTRUMENT_OPTIONS} from 'graphql/queries/assetManagement';
import {
  approvalData,
  matchMention,
  nameList,
  versionsFromArticle,
} from './draftHelpers';
import styles from '../shared/news.module.scss';

// Quill reports its own reformatting of the value as a change. Passing on
// only the editor's keystrokes keeps "touched" meaning the text was edited.
const QuillField = ({value, onChange, readOnly}) => (
  <ReactQuill
    theme="snow"
    value={value || ''}
    readOnly={readOnly}
    modules={QUILL_MODULES}
    formats={QUILL_FORMATS}
    onChange={(html, delta, source) => {
      if (source === 'user') onChange?.(html);
    }}
  />
);

// One input per point, at least three. Empty points are dropped on publish.
const SummaryField = ({value = [], onChange, disabled}) => (
  <div className={styles.choices}>
    {Array.from({length: Math.max(value.length, 3)}, (_, i) => (
      <Input
        key={i}
        value={value[i]}
        disabled={disabled}
        placeholder={DRAFT.point(i + 1)}
        onChange={e => {
          const next = [...value];
          next[i] = e.target.value;
          onChange(next);
        }}
      />
    ))}
  </div>
);

// The story's picture. A new one goes to the same bucket Website News uses,
// and only reaches the story when it is published.
const PictureField = ({value, onChange, disabled, onUploadingChange}) => {
  const [uploading, setUploading] = useState(false);
  const upload = async ({file}) => {
    setUploading(true);
    onUploadingChange(true);
    try {
      const url = await UploadDocumentToBucket(
        await resizeImageFile(file),
        undefined,
        'investor-updates-bucket',
      );
      if (url) onChange?.(url);
      else toast.error(DRAFT.pictureFailed);
    } catch (err) {
      toast.error(DRAFT.pictureFailed);
    } finally {
      setUploading(false);
      onUploadingChange(false);
    }
  };
  return (
    <div className={styles.picture}>
      {value ? (
        <img src={value} alt="" />
      ) : (
        <div className={styles.picturePlaceholder}>{DRAFT.noPicture}</div>
      )}
      {!disabled && (
        <Upload
          accept="image/*"
          showUploadList={false}
          customRequest={upload}
          disabled={uploading}>
          <Button icon={<UploadOutlined />} loading={uploading}>
            {value ? DRAFT.changePicture : DRAFT.addPicture}
          </Button>
        </Upload>
      )}
    </div>
  );
};

const field = (label, tip) => ({label: <Tip label={label} tip={tip} />});

const NewsDraftReview = () => {
  const {draftId} = useParams();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [rejecting, setRejecting] = useState(false);
  const client = useApolloClient();
  const [reason, setReason] = useState(DRAFT.reasons[0].value);
  const [otherReason, setOtherReason] = useState('');
  const [pictureUploading, setPictureUploading] = useState(false);
  const ops = useSelector(state =>
    makeSelectSubTabOperations(state, NavTabsEnum.News, REVIEW_SUB_TAB),
  );

  const {data, loading, error, refetch} = useQuery(GET_NEWS_DRAFT, {
    variables: {draftId},
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    errorPolicy: 'all',
  });
  const response = data?.newsDraft;
  const draft = response?.__typename === 'NewsDraft' ? response : null;
  const failure = response?.__typename === 'Error' ? response : error;

  const initialValues = useMemo(
    () =>
      draft
        ? {
            versions: versionsFromArticle(draft.article),
            type: draft.article?.type,
            tag: draft.article?.tag,
            instrumentIds: draft.article?.instrumentIds || [],
            featuredImage: draft.article?.featuredImage,
          }
        : null,
    [draft],
  );

  // Fill the company picker with the listed companies the writing tool named,
  // found by searching their names. Doing it through setFieldsValue leaves the
  // field untouched, so publishing still lets the backend link companies
  // from the text unless the editor changes them.
  useEffect(() => {
    const mentions = draft?.companyMentions || [];
    if (!mentions.length || draft.article?.instrumentIds?.length) return;
    let cancelled = false;
    const fill = async () => {
      const ids = [];
      for (const mention of mentions) {
        try {
          const {data} = await client.query({
            query: GET_FINANCIAL_INSTRUMENT_OPTIONS,
            variables: {
              input: {
                assetClassKey: 'EQUITIES',
                instrumentKey: 'PUBLIC_EQUITY',
                explore: false,
                status: 'ENABLED',
                paginate: true,
                page: 1,
                limit: 5,
                exchange: draft.exchangeId,
                search: mention.name,
              },
            },
          });
          const match = matchMention(
            mention,
            data?.getFinancialInstruments?.data || [],
          );
          if (match?.id && !ids.includes(match.id)) ids.push(match.id);
        } catch (err) {
          console.error(err);
        }
      }
      if (
        !cancelled &&
        ids.length &&
        !form.isFieldsTouched(['instrumentIds'])
      ) {
        form.setFieldsValue({instrumentIds: ids});
      }
    };
    fill();
    return () => {
      cancelled = true;
    };
  }, [draft, client, form]);

  const [approve, {loading: publishing}] = useMutation(APPROVE_NEWS_DRAFT, {
    onError: actionError('publish this draft'),
  });
  const [reject, {loading: dropping}] = useMutation(REJECT_NEWS_DRAFT, {
    onError: actionError('drop this draft'),
  });

  const pending = draft?.status === 'PENDING_REVIEW';
  // Publishing needs create on Website News, not publishing needs update.
  const canAct = pending && ops?.create;
  const backToList = () => navigate('/dashboard/news/review');

  const publish = () => {
    if (pictureUploading) return toast.info(DRAFT.pictureUploading);
    // Tabs mount their fields on first open, so the submitted values miss a
    // language nobody opened. The whole store has every version.
    const values = form.getFieldsValue(true);
    const touched = values.versions.map((_, i) =>
      form.isFieldsTouched([['versions', i]]),
    );
    const edited =
      touched.some(Boolean) ||
      form.isFieldsTouched([['type'], ['tag'], ['featuredImage']]);
    const companiesTouched = form.isFieldsTouched(['instrumentIds']);
    const onlyOne =
      touched.length === 2 && touched.filter(Boolean).length === 1;
    const editedLanguage = values.versions[touched.indexOf(true)]?.language;
    const otherLanguage = values.versions[touched.indexOf(false)]?.language;

    Modal.confirm({
      title: DRAFT.publishTitle,
      content: onlyOne
        ? DRAFT.oneLanguageEdited(
            LANGUAGE_LABEL[editedLanguage],
            LANGUAGE_LABEL[otherLanguage],
          )
        : edited || companiesTouched
        ? DRAFT.publishEdited
        : DRAFT.publishAsIs,
      okText: onlyOne ? 'Publish as it is' : DRAFT.publish,
      onOk: async () => {
        const {data: result} = await approve({
          variables: {
            draftId,
            data: approvalData({
              article: draft.article,
              values,
              edited,
              companiesTouched,
            }),
          },
        });
        const payload = result?.approveNewsDraft;
        if (!payload) return;
        // These come back readable ("this web address is already taken"),
        // and the draft stays waiting so the editor can fix it.
        if (payload.__typename === 'Error') return toast.error(payload.message);
        toast.success(DRAFT.published(values.versions[0]?.title));
        refetch();
      },
    });
  };

  const drop = async () => {
    const {data: result} = await reject({
      variables: {draftId, reason, note: otherReason.trim() || undefined},
    });
    const payload = result?.rejectNewsDraft;
    if (!payload) return;
    if (payload.__typename === 'Error') {
      return unionError('drop this draft', payload);
    }
    toast.success(DRAFT.rejectedToast);
    setRejecting(false);
    refetch();
  };

  if (loading && !draft) return <Spin className={styles.spinner} />;

  if (!draft) {
    return (
      <div className={styles.root}>
        <Button icon={<ArrowLeftOutlined />} onClick={backToList}>
          {DRAFT.back}
        </Button>
        <Alert
          type={notDeployed(failure?.message) ? 'info' : 'error'}
          showIcon
          message={loadError('this draft', failure)}
        />
      </div>
    );
  }

  const status = STATUS[draft.status];
  const outlets = (draft.sources || [])
    .map(source => source.outlet)
    .filter((outlet, i, all) => outlet && all.indexOf(outlet) === i);

  return (
    <div className={styles.root}>
      <Button icon={<ArrowLeftOutlined />} onClick={backToList}>
        {DRAFT.back}
      </Button>

      <div className={styles.toolbar}>
        <div>
          <h2 className={styles.title}>{draft.title}</h2>
          <p className={styles.muted}>
            {status ? <Tag color={status.color}>{status.label}</Tag> : null}
            {DRAFT.from(nameList(outlets))} ·{' '}
            {DRAFT.written(moment(draft.createdAt).fromNow())}
          </p>
          {pending && (draft.requestedBy || draft.heldBecause?.length) ? (
            <p className={styles.reason}>
              {draft.requestedBy
                ? DRAFT.writtenOnRequest
                : DRAFT.heldBecause(nameList(draft.heldBecause))}
            </p>
          ) : null}
        </div>
      </div>

      {draft.status === 'PUBLISHED' && draft.publishedWebsiteNewsId ? (
        <Alert
          type="success"
          showIcon
          message={DRAFT.publishedAlert}
          description={
            draft.publishedWithEdits
              ? DRAFT.publishedWithEdits
              : DRAFT.publishedAsWritten
          }
          action={
            <Button
              size="small"
              onClick={() =>
                navigate(
                  `/dashboard/news/update-website-news/${draft.publishedWebsiteNewsId}/${draft.language}`,
                )
              }>
              {DRAFT.openArticle}
            </Button>
          }
        />
      ) : null}
      {draft.status === 'REJECTED' ? (
        <Alert
          type="warning"
          showIcon
          message={DRAFT.rejected}
          description={[
            DRAFT.reasons.find(r => r.value === draft.rejectReason)?.label,
            draft.rejectNote,
          ]
            .filter(Boolean)
            .join(': ')}
        />
      ) : null}

      <div className={styles.reviewSplit}>
        <div className={styles.panel}>
          <p className={styles.muted}>{DRAFT.bothLanguages}</p>
          <Form
            form={form}
            layout="vertical"
            onFinish={publish}
            initialValues={initialValues}
            disabled={!canAct}>
            <Form.Item
              name="featuredImage"
              {...field(DRAFT.picture, DRAFT.pictureTip)}>
              <PictureField
                disabled={!canAct}
                onUploadingChange={setPictureUploading}
              />
            </Form.Item>
            <Tabs>
              {initialValues.versions.map((version, index) => (
                <Tabs.TabPane
                  key={version.language || index}
                  tab={LANGUAGE_LABEL[version.language] || version.language}>
                  <Form.Item
                    name={['versions', index, 'title']}
                    {...field(DRAFT.headline, DRAFT.headlineTip)}
                    rules={[{required: true, message: DRAFT.headlineEmpty}]}>
                    <Input />
                  </Form.Item>
                  <Form.Item
                    name={['versions', index, 'content']}
                    {...field(DRAFT.story, DRAFT.storyTip)}>
                    <QuillField readOnly={!canAct} />
                  </Form.Item>
                  <Form.Item
                    name={['versions', index, 'keytakes']}
                    {...field(DRAFT.keyTakeaway, DRAFT.keyTakeawayTip)}>
                    <QuillField readOnly={!canAct} />
                  </Form.Item>
                  <Form.Item
                    name={['versions', index, 'summary']}
                    {...field(DRAFT.summary, DRAFT.summaryTip)}>
                    <SummaryField />
                  </Form.Item>
                  <Form.Item
                    name={['versions', index, 'metaDescription']}
                    {...field(
                      DRAFT.searchDescription,
                      DRAFT.searchDescriptionTip,
                    )}>
                    <Input.TextArea rows={2} showCount maxLength={160} />
                  </Form.Item>
                  <Form.Item
                    name={['versions', index, 'imageAltText']}
                    {...field(
                      DRAFT.imageDescription,
                      DRAFT.imageDescriptionTip,
                    )}>
                    <Input />
                  </Form.Item>
                  <Form.Item
                    name={['versions', index, 'slug']}
                    {...field(DRAFT.webAddress, DRAFT.webAddressTip)}>
                    <Input />
                  </Form.Item>
                  <Form.Item
                    name={['versions', index, 'keywords']}
                    {...field(DRAFT.keywords, DRAFT.keywordsTip)}>
                    <Select mode="tags" open={false} />
                  </Form.Item>
                </Tabs.TabPane>
              ))}
            </Tabs>
            <div className={styles.formGrid}>
              <Form.Item name="type" {...field(DRAFT.type, DRAFT.typeTip)}>
                <Select options={TYPE_OPTIONS} />
              </Form.Item>
              <Form.Item name="tag" {...field(DRAFT.tag, DRAFT.tagTip)}>
                <Select options={TAG_OPTIONS} />
              </Form.Item>
            </div>
            <Form.Item
              name="instrumentIds"
              {...field(DRAFT.companies, DRAFT.companiesTip)}>
              <CompanySearch exchangeId={draft.exchangeId} />
            </Form.Item>
          </Form>
        </div>

        <div className={styles.panel}>
          <h3>
            <Tip label={DRAFT.sources} tip={DRAFT.sourcesTip} />
          </h3>
          <ul className={styles.cardList}>
            {(draft.sources || []).map(source => (
              <li key={source.articleId || source.url}>
                <a href={source.url} target="_blank" rel="noreferrer">
                  <strong>{source.outlet}</strong> ↗
                </a>
                <div>{source.title}</div>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {pending ? (
        <div className={styles.toolbar}>
          <span />
          <div className={styles.cardActions}>
            <ActionButton
              danger
              allowed={ops?.update}
              tip={DRAFT.rejectTip}
              onClick={() => setRejecting(true)}>
              {DRAFT.reject}
            </ActionButton>
            <ActionButton
              type="primary"
              allowed={ops?.create}
              tip={DRAFT.publishTip}
              loading={publishing}
              disabled={pictureUploading}
              onClick={() => form.submit()}>
              {DRAFT.publish}
            </ActionButton>
          </div>
        </div>
      ) : null}

      <NewsModal isOpen={rejecting} onClose={() => setRejecting(false)}>
        <div className={styles.modalRoot}>
          <h2>{DRAFT.reject}</h2>
          <h4>
            <Tip label={DRAFT.reason} tip={DRAFT.reasonTip} />
          </h4>
          <ChoiceGroup
            value={reason}
            onChange={setReason}
            options={DRAFT.reasons}
          />
          <div className={styles.block}>
            <p className={styles.questionLabel}>
              {reason === 'OTHER' ? DRAFT.other : DRAFT.otherOptional}
            </p>
            <Input.TextArea
              rows={2}
              aria-label={
                reason === 'OTHER' ? DRAFT.other : DRAFT.otherOptional
              }
              value={otherReason}
              onChange={e => setOtherReason(e.target.value)}
            />
          </div>
          <div className={styles.modalActions}>
            <ActionButton
              danger
              type="primary"
              allowed={ops?.update}
              tip={DRAFT.rejectTip}
              loading={dropping}
              onClick={drop}>
              {DRAFT.reject}
            </ActionButton>
          </div>
        </div>
      </NewsModal>
    </div>
  );
};

export default NewsDraftReview;
